import { Timers } from '../../../timers';
import { createDebugLogger } from '../../../debug-logger';
import { UserProperties, CompanyProperties } from './types';
import { AnalyticsUserProperties } from './analytics.constants';
import { differenceInYears, parseISO } from 'date-fns';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

export type Options = {
  appName: string;
  measurementId: string;
  debug?: boolean;
  AWSAnalytics?: any;
};

type AnalyticsTimersData = {
  category: string;
};

export class Analytics {
  _timers: Timers<AnalyticsTimersData>;
  _logger: any;
  _options: Options;
  _persistentSettings: any;
  _previousScreenName = '';

  constructor(options: Options) {
    this._persistentSettings = {};
    this._options = options;
    /**
     * Used by the `timing_complete` event used to track elapsed time
     * on different events
     * @type {Timers<AnalyticsTimersData>}
     * @private
     */
    this._timers = new Timers<AnalyticsTimersData>({
      debug: this._options.debug,
    });
    this._logger = createDebugLogger('[Analytics]:', !this._options.debug);
  }

  updateAnalyticsConsent = (newValue: boolean) => {
    window[`ga-disable-${this._options.measurementId}`] = !newValue;
  };

  trackScreenView(screenName: string, section: string, queryParams?: any) {
    /**
     * This check prevents double counting the same `screen_view`
     *
     * The router state update is being invoked twice with the same value,
     * in some situations. Multiple invocations
     *
     * (ie: User clicks on the same menu item)
     */
    if (this._previousScreenName !== screenName) {
      const allowedGaTags = ['utm_campaign', 'utm_medium', 'utm_source'];
      const params = pick(queryParams, allowedGaTags);

      const query = queryString.stringify(params);

      /**
       * Configuration of `page_path` `page_title` and `page_location`
       * should happen before firing events because when GA batches events
       * it will use the config it had at the beginning of the batching.
       */
      this._config({
        page_location: isEmpty(params) ? screenName : `${screenName}?${query}`,
        page_title: screenName,
        page_referrer: this._previousScreenName,
        page_path: screenName,
      });
      /**
       * If we have a previous screen name we fire the timer started
       * on the previous screen and then remove it.
       */
      if (this._previousScreenName) {
        const timerName = this._previousScreenName;
        const timer = this._timers.get(timerName);

        if (timer) {
          this.trackTiming(timer.name, timer.elapsedMs, timer.data.category);
          this._timers.delete(timerName);
        }
      }

      this._previousScreenName = screenName;

      /**
       * Store a timer with the current screen as reference to be able to
       * calculate the difference when the user navigates to a new screen
       */
      this._timers.start(screenName, {
        category: 'time_on_screen',
      });

      this.trackEvent('screen_view', {
        app_name: this._options.appName,
        screen_name: screenName,
        section,
      });
    }
  }

  trackTiming(name: string, value: number, category?: string) {
    this.trackEvent('timing_complete', {
      name,
      value,
      event_category: category,
    });
  }

  setUser = (user: UserProperties) => {
    /**
     * If we do not have a anon_user_id it means we are not logged in.
     * We are putting all properties to null
     */
    if (!user.anon_user_id) {
      this._config({ [AnalyticsUserProperties.UserID]: null });

      gtag('set', 'user_properties', {
        [AnalyticsUserProperties.UserGender]: null,
        [AnalyticsUserProperties.UserAge]: null,
        [AnalyticsUserProperties.UserAccountType]: null,
        [AnalyticsUserProperties.UserOriginator]: null,
        [AnalyticsUserProperties.UserRole]: null,
      });
      return;
    }

    const dataOfBirth = user.birthday_date;
    const userAge = dataOfBirth
      ? differenceInYears(Date.now(), parseISO(`${dataOfBirth}`))
      : null;

    /**
     * User ID is not a user property and must be set by updating the config according to:
     * https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id#set_user_id
     *
     * > To implement the User ID with gtag.js, update the config for your property to set the User ID:
     * >
     * > ```
     * > gtag('config', 'GA_MEASUREMENT_ID', {
     * >  'user_id': 'USER_ID'
     * > });
     * > ```
     */
    this._config({ [AnalyticsUserProperties.UserID]: user.anon_user_id });

    /**
     * If we have a value we set it otherwise we are setting to `null`
     */
    gtag('set', 'user_properties', {
      [AnalyticsUserProperties.UserGender]: user.gender || null,
      [AnalyticsUserProperties.UserAge]: Number.isInteger(userAge)
        ? userAge
        : null,
      [AnalyticsUserProperties.UserAccountType]: user.account_type || null,
      [AnalyticsUserProperties.UserOriginator]: user.originator || null,
      [AnalyticsUserProperties.UserRole]: user.role || null,
    });
  };

  setCompanyProperties = (company: CompanyProperties) => {
    /** TODO: Look into how could get around the fact that we start reporting
     *        the company user properties as soon as we have them but when we get them
     *        from the tenancy-lookup in the branding we don't have the required flags
     *        to figure out the company type.
     *
     */
    const getCompanyType = (company: CompanyProperties) => {
      if (!company.company_id) {
        return;
      }
      if (company.demo) {
        return 'demo';
      }
      if (company.internal) {
        return 'test';
      }
      return 'live';
    };

    gtag('set', 'user_properties', {
      [AnalyticsUserProperties.CompanyID]: company.company_id || null,
      [AnalyticsUserProperties.CompanyName]: company.name || null,
      [AnalyticsUserProperties.CompanyAccountType]:
        getCompanyType(company) || null,
    });
  };

  trackEvent(event: string, eventData: any) {
    if (this._options.AWSAnalytics) {
      gtag(
        'get',
        this._options.measurementId,
        'user_properties',
        userAttributes => {
          const recordEvent = {
            name: event,
            attributes: {
              ...eventData,
              ...userAttributes,
              userId: this._persistentSettings[AnalyticsUserProperties.UserID],
            },
          } as const;
          if (this._options.AWSAnalytics) {
            this._options.AWSAnalytics.record(recordEvent);
          }
        },
      );
    }
    gtag('event', event, eventData);
  }

  _config(persistentSettings: any /* gtag config options */) {
    this._persistentSettings = {
      ...this._persistentSettings,
      ...persistentSettings,
    };

    gtag('config', this._options.measurementId, {
      ...this._persistentSettings,
      /**
       * every time we call config, by default it will automatically send a
       * `page_view` unless we disable it, which we want to..
       */
      send_page_view: false,
      anonymize_ip: true,
    });
  }
}

let instance;

export default (options: Options) => {
  return (instance = instance || new Analytics(options));
};
